import { jsx as _jsx } from "react/jsx-runtime";
import { FlagOutlined } from "@ant-design/icons";
import { ETranslationFlag, TRANSLATION_FLAG_SUFFIX } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EServiceTemplateGoogleConsentModeTypes, ESupportedLanguage } from "@devowl-wp/api-real-cookie-banner";
import { SvgCroatia, SvgCzechRepublic, SvgDenmark, SvgFinland, SvgFrance, SvgGermany, SvgGermanyFormal, SvgGreece, SvgHungary, SvgItaly, SvgNetherlands, SvgNetherlandsFormal, SvgNorway, SvgPoland, SvgPortugal, SvgRomania, SvgSlovakia, SvgSlovenia, SvgSpain, SvgSweden, SvgUnitedKingdom } from "@devowl-wp/react-frontend";
import { getValueByNamePath } from "@devowl-wp/react-utils";
import { mergeStyles } from "./style.js";
import { LanguageIcon } from "../components/route/real-cookie-banner/template/fields/translation/language-icon-addon/language-icon.js";
import { colors } from "../styles/colors.js";
import { spacing } from "../styles/spacing.js";
import { defaultTranslationFlexNamePath } from "./template/form.js";
/**
 * Field name order mapping, frozen to prevent modification.
 * Maps field names to their order positions.
 */ const FIELD_NAME_ORDER_MAP = {
    headline: 10,
    subHeadline: 20,
    name: 30,
    // Service template fields - START
    groupNotice: 40,
    providerNotice: 50,
    provider: 60,
    providerPrivacyPolicyUrl: 70,
    providerLegalNoticeUrl: 80,
    purposes: 90,
    legalBasisNotice: 100,
    technicalDefinitions: 110,
    technicalHandlingNotice: 120,
    dynamicFields: 130,
    createContentBlockerNotice: 140,
    // Service template fields - END
    // Content blocker template fields - START
    description: 150,
    ruleNotice: 155,
    visualHeroButtonText: 160
};
Object.freeze(FIELD_NAME_ORDER_MAP);
/**
 * Returns the order position for a given field name.
 *
 * @param fieldName - The name of the field.
 * @returns The order position of the field, or MAX_SAFE_INTEGER if not found.
 */ function getFieldNameOrderPosition(fieldName) {
    return FIELD_NAME_ORDER_MAP[fieldName] || Number.MAX_SAFE_INTEGER;
}
/**
 * Computes the style for a translation flag element based on the translation flags associated with a specific field in a form.
 *
 * @param form - The form instance from which to retrieve the translation flag values.
 * @param absoluteNamePath - The path in the form's data structure where the translation flags are referred to.
 * @returns The computed style object to be applied to the translation flag element. If no flags are found, returns an empty style object.
 */ function getTranslationFlagElementStyle(form, absoluteNamePath) {
    let style = {};
    if (form) {
        const translationFlagNamePath = getTranslationFlagNamePath(absoluteNamePath);
        const flags = form.getFieldValue(translationFlagNamePath);
        if (Array.isArray(flags) && flags.length > 0) {
            if (flags.includes(ETranslationFlag.MachineTranslated)) {
                style = {
                    ...DEFAULT_TRANSLATION_MACHINE_TRANSLATED_FLAG_STYLE
                };
            } else if (flags.includes(ETranslationFlag.Fuzzy)) {
                style = {
                    ...DEFAULT_TRANSLATION_FUZZY_FLAG_STYLE
                };
            } else {
                style = {
                    ...DEFAULT_TRANSLATION_HAS_FLAGS_STYLE
                };
            }
        }
    }
    return style;
}
/**
 * Determines the appropriate form item properties based on the current language settings and mode.
 *
 * @param current - The current language to evaluate for form item visibility.
 * @param settings - Current settings for the form translation.
 * @returns An object containing form item properties, specifically the style property to control the display of the form item.
 */ function getFormPropsByLanguage(current, settings) {
    const { sourceLanguage, targetLanguage, isTranslationMode } = settings;
    const displayLanguages = [
        sourceLanguage,
        targetLanguage,
        DEFAULT_LANGUAGE
    ];
    const props = {};
    if (isTranslationMode) {
        if (!displayLanguages.includes(current)) {
            props.style = {
                display: "none"
            };
        }
    } else if (current !== DEFAULT_LANGUAGE) {
        props.style = {
            display: "none"
        };
    }
    return props;
}
/**
 * Determines the appropriate input properties based on the current language settings and mode.
 *
 * @param current - The current language to evaluate for input properties.
 * @param settings - Current settings for the form translation.
 * @returns An object containing input properties, specifically the style property to control the display of the input.
 */ function getInputPropsByLanguage(current, settings) {
    const { sourceLanguage, targetLanguage, isTranslationMode } = settings;
    const displayLanguages = [
        sourceLanguage,
        targetLanguage,
        DEFAULT_LANGUAGE
    ];
    const props = {
        style: {
            marginBottom: spacing.layout.content.marginSmall,
            marginTop: spacing.layout.content.marginSmall
        }
    };
    if (isTranslationMode) {
        if (!displayLanguages.includes(current)) {
            props.style = {
                display: "none"
            };
        }
    } else if (current !== DEFAULT_LANGUAGE) {
        props.style = {
            display: "none"
        };
    }
    return props;
}
/**
 * Determines the appropriate select properties based on the current language settings and mode.
 *
 * @param current - The current language to evaluate for select properties.
 * @param settings - Current settings for the form translation.
 * @returns An object containing select properties, specifically the style property to control the display of the select.
 */ function getSelectPropsByLanguage(current, settings) {
    const { sourceLanguage, targetLanguage, isTranslationMode } = settings;
    const displayLanguages = [
        sourceLanguage,
        targetLanguage,
        DEFAULT_LANGUAGE
    ];
    const props = {
        style: {
            marginBottom: spacing.layout.content.marginSmall,
            marginTop: spacing.layout.content.marginSmall
        }
    };
    if (isTranslationMode) {
        if (!displayLanguages.includes(current)) {
            props.style = {
                display: "none"
            };
        }
    } else if (current !== DEFAULT_LANGUAGE) {
        props.style = {
            display: "none"
        };
    }
    return props;
}
/**
 * Retrieves default properties for the Quill editor wrapper based on the language settings and form instance.
 *
 * @param namePath - The name path for the field in the form.
 * @param form - The form instance.
 * @returns An object containing properties for the Quill editor wrapper.
 */ function getQuillEditorWrapperPropsDefaultLang(namePath, form) {
    const props = {
        onChange: ()=>onTranslationValueChange(form, namePath)
    };
    props.style = getTranslationFlagElementStyle(form, namePath);
    return props;
}
/**
 * Retrieves default properties for input based on the language settings and form instance.
 *
 * @param settings - Current settings for the form translation.
 * @param namePathString - The name path string for the field in the form.
 * @param form - The form instance.
 * @returns An object containing properties for the input.
 */ function getInputPropsDefaultLang(settings, namePathString, form) {
    const inputProps = getInputPropsByLanguage(DEFAULT_LANGUAGE, settings);
    // onchange delete translation flags
    inputProps.onChange = ()=>onTranslationValueChange(form, namePathString);
    // styling
    const translationFlagStyle = getTranslationFlagElementStyle(form, namePathString);
    inputProps.style = mergeStyles(inputProps.style, translationFlagStyle);
    // language icon
    inputProps.suffix = /*#__PURE__*/ _jsx(LanguageIcon, {
        lang: DEFAULT_LANGUAGE,
        flexPathConfig: {
            absolute: {
                namePath: namePathString,
                translationNamePath: defaultTranslationFlexNamePath(namePathString)
            }
        }
    });
    return inputProps;
}
/**
 * Resolves the SVG icon for a given language.
 *
 * @param lang - The language for which to get the icon.
 * @param props - Optional SVG props to apply to the icon.
 * @returns The SVG icon for the language.
 */ function getLanguageIcon(lang, props = {}) {
    switch(lang){
        case ESupportedLanguage.de:
        case ESupportedLanguage.de_formal:
            return /*#__PURE__*/ _jsx(SvgGermanyFormal, {
                ...props
            });
        case ESupportedLanguage.de_informal:
            return /*#__PURE__*/ _jsx(SvgGermany, {
                ...props
            });
        case ESupportedLanguage.en:
            return /*#__PURE__*/ _jsx(SvgUnitedKingdom, {
                ...props
            });
        case ESupportedLanguage.fr:
            return /*#__PURE__*/ _jsx(SvgFrance, {
                ...props
            });
        case ESupportedLanguage.es:
            return /*#__PURE__*/ _jsx(SvgSpain, {
                ...props
            });
        case ESupportedLanguage.it:
            return /*#__PURE__*/ _jsx(SvgItaly, {
                ...props
            });
        case ESupportedLanguage.nl:
        case ESupportedLanguage.nl_formal:
            return /*#__PURE__*/ _jsx(SvgNetherlandsFormal, {
                ...props
            });
        case ESupportedLanguage.nl_informal:
            return /*#__PURE__*/ _jsx(SvgNetherlands, {
                ...props
            });
        case ESupportedLanguage.da:
            return /*#__PURE__*/ _jsx(SvgDenmark, {
                ...props
            });
        case ESupportedLanguage.sv:
            return /*#__PURE__*/ _jsx(SvgSweden, {
                ...props
            });
        case ESupportedLanguage.no:
            return /*#__PURE__*/ _jsx(SvgNorway, {
                ...props
            });
        case ESupportedLanguage.cs:
            return /*#__PURE__*/ _jsx(SvgCzechRepublic, {
                ...props
            });
        case ESupportedLanguage.pt:
            return /*#__PURE__*/ _jsx(SvgPortugal, {
                ...props
            });
        case ESupportedLanguage.pl:
            return /*#__PURE__*/ _jsx(SvgPoland, {
                ...props
            });
        case ESupportedLanguage.el:
            return /*#__PURE__*/ _jsx(SvgGreece, {
                ...props
            });
        case ESupportedLanguage.rm:
            return /*#__PURE__*/ _jsx(SvgRomania, {
                ...props
            });
        case ESupportedLanguage.sk:
            return /*#__PURE__*/ _jsx(SvgSlovakia, {
                ...props
            });
        case ESupportedLanguage.fi:
            return /*#__PURE__*/ _jsx(SvgFinland, {
                ...props
            });
        case ESupportedLanguage.hu:
            return /*#__PURE__*/ _jsx(SvgHungary, {
                ...props
            });
        case ESupportedLanguage.hr:
            return /*#__PURE__*/ _jsx(SvgCroatia, {
                ...props
            });
        case ESupportedLanguage.sl:
            return /*#__PURE__*/ _jsx(SvgSlovenia, {
                ...props
            });
        default:
            return /*#__PURE__*/ _jsx(FlagOutlined, {});
    }
}
/**
 * Translates Google consent mode types to their respective descriptions.
 *
 * @param type - The Google consent mode type.
 * @param t - The translation function.
 * @returns The translated description for the given consent mode type.
 */ function getGoogleConsentModeTypeTranslation(type, t) {
    switch(type){
        case EServiceTemplateGoogleConsentModeTypes.AdStorage:
            return t("Storing and reading of data such as cookies (web) or device identifiers (apps) related to advertising.");
        case EServiceTemplateGoogleConsentModeTypes.AdUserData:
            return t("Sending user data to Google for online advertising purposes.");
        case EServiceTemplateGoogleConsentModeTypes.AdPersonalization:
            return t("Evaluation and display of personalized advertising.");
        case EServiceTemplateGoogleConsentModeTypes.AnalyticsStorage:
            return t("Storing and reading of data such as cookies (web) or device identifiers (apps), related to analytics (e.g. visit duration).");
        case EServiceTemplateGoogleConsentModeTypes.FunctionalityStorage:
            return t("Storing and reading of data that supports the functionality of the website or app (e.g. language settings).");
        case EServiceTemplateGoogleConsentModeTypes.PersonalizationStorage:
            return t("Storing and reading of data related to personalization (e.g. video recommendations).");
        case EServiceTemplateGoogleConsentModeTypes.SecurityStorage:
            return t("Storing and reading of data related to security (e.g. authentication functionality, fraud prevention, and other user protection).");
        default:
            return t("No translation found!");
    }
}
/**
 * Appends a translation flag suffix to a name path.
 *
 * @param namePath - The original name path.
 * @returns The name path with the translation flag suffix appended.
 */ function getTranslationFlagNamePath(namePath) {
    let result = namePath;
    if (result) {
        if (Array.isArray(result)) {
            // copy
            result = [
                ...result
            ];
            const lastPart = result.pop();
            result.push(`${lastPart}${TRANSLATION_FLAG_SUFFIX}`);
        } else {
            result = `${namePath}${TRANSLATION_FLAG_SUFFIX}`;
        }
    }
    return result;
}
/**
 * Handles changes to translation-related values within a form by updating the translation flags.
 *
 * @param form - The form instance on which operations are to be performed.
 * @param absoluteNamePath - The path to the field within the form data where the translation flags are stored.
 * @param targetLanguage - The target language to update the translation flags for.
 * @param flagsToKeep - Optional array of flags that should be retained in the field value. Defaults to an empty array if not provided.
 */ function onTranslationValueChange(form, absoluteNamePath, targetLanguage, flagsToKeep = []) {
    if (form && absoluteNamePath) {
        const translationFlagNamePath = getTranslationFlagNamePath(absoluteNamePath);
        if (translationFlagNamePath) {
            const currentValue = form.getFieldValue(translationFlagNamePath);
            if (Array.isArray(currentValue) && currentValue.length > 0) {
                const filtered = currentValue.filter((flag)=>flagsToKeep.includes(flag));
                form.setFieldValue(translationFlagNamePath, filtered);
            }
        }
    }
}
/**
 * Default style for elements that have translation flags.
 */ const DEFAULT_TRANSLATION_HAS_FLAGS_STYLE = {
    border: `1px solid ${colors.translation.hasFlags}6b`,
    boxShadow: `1px 1px ${colors.translation.hasFlags}6b`
};
Object.freeze(DEFAULT_TRANSLATION_HAS_FLAGS_STYLE);
/**
 * Default style for elements that has fuzzy translation flag.
 */ const DEFAULT_TRANSLATION_FUZZY_FLAG_STYLE = {
    border: `1px solid ${colors.translation.fuzzy}6b`,
    boxShadow: `1px 1px ${colors.translation.fuzzy}6b`
};
Object.freeze(DEFAULT_TRANSLATION_FUZZY_FLAG_STYLE);
/**
 * Default style for elements that has machine-translated translation flag.
 */ const DEFAULT_TRANSLATION_MACHINE_TRANSLATED_FLAG_STYLE = {
    border: `1px solid ${colors.translation.machineTranslated}6b`,
    boxShadow: `1px 1px ${colors.translation.machineTranslated}6b`
};
Object.freeze(DEFAULT_TRANSLATION_MACHINE_TRANSLATED_FLAG_STYLE);
/**
 * Retrieves translation flags from template metadata.
 *
 * @param data - The template metadata.
 * @returns An object containing the translation flags.
 */ function getTemplateTranslationFlags(data) {
    const result = {};
    for (const key of Object.keys(data)){
        const value = data[key];
        // Directly process strings that match the suffix
        if (key.endsWith(TRANSLATION_FLAG_SUFFIX)) {
            result[key] = value;
        } else if (value !== null) {
            if (typeof value === "object") {
                if (Array.isArray(value)) {
                    // Map each object in the array and filter out empty results
                    const arrayResult = value.map((item)=>{
                        if (typeof item === "object" && !Array.isArray(item)) {
                            return getTemplateTranslationFlags(item);
                        }
                        return null;
                    }).filter((item)=>item !== null && Object.keys(item).length > 0);
                    if (arrayResult.length > 0) {
                        result[key] = arrayResult;
                    }
                } else {
                    // Process non-array objects
                    const filteredSubObject = getTemplateTranslationFlags(value);
                    if (Object.keys(filteredSubObject).length > 0) {
                        result[key] = filteredSubObject;
                    }
                }
            }
        }
    }
    return result;
}
/**
 * Checks if translation flags have changed between two template metadata objects.
 *
 * @param prev - The previous template metadata object.
 * @param next - The next template metadata object.
 * @param path - The name path to check for changes.
 * @returns True if the translation flags have changed, false otherwise.
 */ function hasTranslationFlagsChanged(prev, next, path) {
    const translationFlagNamePath = path ? getTranslationFlagNamePath(path) : null;
    const prevValue = translationFlagNamePath ? getValueByNamePath(prev, translationFlagNamePath) : getTemplateTranslationFlags(prev);
    const nextValue = translationFlagNamePath ? getValueByNamePath(next, translationFlagNamePath) : getTemplateTranslationFlags(next);
    // Compare objects directly and then stringify if necessary
    if (prevValue === nextValue) return false;
    return JSON.stringify(prevValue) !== JSON.stringify(nextValue);
}
export { getFieldNameOrderPosition, getFormPropsByLanguage, getGoogleConsentModeTypeTranslation, getInputPropsByLanguage, getInputPropsDefaultLang, getLanguageIcon, getQuillEditorWrapperPropsDefaultLang, getSelectPropsByLanguage, getTemplateTranslationFlags, getTranslationFlagElementStyle, getTranslationFlagNamePath, hasTranslationFlagsChanged, onTranslationValueChange };
